import { Component, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { PopUpNotificationComponent } from '@components/pop-up-notification/pop-up-notification.component';
import { AuthService } from '@service/auth.service';
import { getValidationErrors } from 'ops/common/validators';
import { HttpStatusCode } from '@angular/common/http';
import { passwordValidate } from 'ops/common/validators/password/password.validator';
import { TranslateService } from '@ngx-translate/core';
import { MatIconRegistry } from '@angular/material/icon';
import { catchError, map, of } from 'rxjs';

@Component({
    selector: 'app-signin-password',
    templateUrl: './signin-password.component.html',
    styleUrls: ['./signin-password.component.scss'],
    standalone: false
})
export class SigninPasswordComponent {
    readonly PASSWORD_MINIMUM = 8;
    readonly PASSWORD_MAXIMUM = 64;

    visibleSigninModal: boolean = false;
    roleType: string;
    passwordVisible: boolean = false;
    passwordValue: string = '';
    signInErrorMsg: string = '';
    showLearnMore: boolean = false;
    isSwitchAccounts: boolean = false;
    selecteRole: { role: string; id: string };
    btnDisable: boolean = false;

    @Output() goBack = new EventEmitter();
    @Output() onCloseSidePanel = new EventEmitter();
    @Output() onSetSidePanelEvent = new EventEmitter();

    @ViewChild('signInConfirm')
    signInConfirm: PopUpNotificationComponent;

    constructor(
        private authService: AuthService,
        private trasnlateService: TranslateService,
        private iconRegistry: MatIconRegistry,
    ) {
        this.roleType = authService.getCurrentUserRoleType();
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnterKey(event: KeyboardEvent) {
        this.onSigninClick();
    }

    closeOverlay() {
        this.visibleSigninModal = false;
        this.showLearnMore = false;
        this.initialValue();
        this.passwordValue = '';
        this.onSetSidePanelEvent.emit();
    }

    public setVisible(
        visible: boolean,
        selecteRole: { role: string; id: string },
        isSwitchAccounts: boolean,
    ) {
        this.visibleSigninModal = visible;
        this.selecteRole = selecteRole;
        this.isSwitchAccounts = isSwitchAccounts;
        this.initialValue();
    }

    initialValue() {
        this.btnDisable = false;
        this.signInErrorMsg = '';
        this.passwordVisible = false;
    }

    onPasswordVisible() {
        this.passwordVisible = !this.passwordVisible;
    }

    onGoBackClick() {
        this.visibleSigninModal = false;
        this.initialValue();
        this.passwordValue = '';
        this.goBack.emit();
    }

    onShowLearnMore() {
        this.showLearnMore = !this.showLearnMore;
    }

    onExitClick() {
        this.visibleSigninModal = false;
        this.authService.saveCurrentUser()
    }

    async onSigninClick() {
        try {
            this.btnDisable = true;
            const validationErrors = this.passwordValidate();
            if (validationErrors.length > 0) {
                throw validationErrors[0];
            }
            await this.authService.signIn(this.selecteRole.id, this.passwordValue);
            this.visibleSigninModal = false;
            this.signInConfirm.setVisible(true);
            this.initialValue();
            this.passwordValue = '';
            this.onCloseSidePanel.emit();
        } catch (error) {
            if (error.status == HttpStatusCode.Unauthorized) {
                this.signInErrorMsg = this.trasnlateService.instant('dlg.signin.password.invalid');
            } else {
                this.signInErrorMsg = this.trasnlateService.instant(error.message, {
                    min: this.PASSWORD_MINIMUM,
                    max: this.PASSWORD_MAXIMUM,
                });
            }
        }
    }

    onSignInEnter(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.onSigninClick();
        }
    }

    onPasswordChange() {
        if (!this.passwordValue.trim()) {
            this.initialValue();
        } else {
            let validation = this.passwordValidate();
            if (validation.length > 0) {
                this.signInErrorMsg = this.trasnlateService.instant(validation[0].message, {
                    min: this.PASSWORD_MINIMUM,
                    max: this.PASSWORD_MAXIMUM,
                });
            } else {
                this.btnDisable = false;
                this.signInErrorMsg = '';
            }
        }
    }

    passwordValidate() {
        return getValidationErrors(passwordValidate, this.passwordValue);
    }

    isIconLoaded(icon: string): boolean {
        let iconExists = false;

        this.iconRegistry
            .getNamedSvgIcon(icon)
            .pipe(
                map(() => {
                    iconExists = true;
                }),
                catchError(() => {
                    iconExists = false;
                    return of(null);
                }),
            )
            .subscribe();

        return iconExists;
    }

    showPassword() {
        return this.passwordVisible && this.isIconLoaded('password-watch');
    }
}
